import { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTableCenteredCell } from "@/shared/ui/DataTable";
import { Button } from "@/shared/ui/button";
import { usePauseModeratorAccountHead } from "@/entities/moderator/api/api";
import { Spinner } from "@/shared/ui/spinner";
import { getUSDTBalance } from "@/entities/balance/utils/get-balance";
import { UnnasignedTgTooltip } from "@/features/UnnasignedTgTooltip";
import { HandleSelectDPUserButton } from "@/pages/restricted/shared/main-page/ui/head/HeadDashboardProDataTable";
import { Roles } from "@/entities/user";
import type { Moderator } from "@/entities/moderator/model";
import { useIsCurrentlyAtDashboardPro } from "@/shared/hooks";

export const ModeratorsColumns: ColumnDef<Moderator>[] = [
  {
    accessorKey: "username",
    header: () => <DataTableCenteredCell>Логин</DataTableCenteredCell>,
    cell: ({ row }) => {
      const isCurrentlyAtDashboardPro = useIsCurrentlyAtDashboardPro([
        Roles.Head,
      ]);

      const navigate = useNavigate();
      const handleNavigate = () => {
        navigate(`/head/access?search=${row.original.username}`);
      };

      return (
        <DataTableCenteredCell>
          <div
            className={`relative ${row.original.tgId === null ? "mr-2" : ""}`}
          >
            {isCurrentlyAtDashboardPro ? (
              <HandleSelectDPUserButton
                userId={row.original.userId ?? ""}
                userName={row.original.username ?? ""}
                userRole={Roles.Moderator}
              />
            ) : (
              <>
                <Button
                  onClick={handleNavigate}
                  variant="link"
                  className="text-blue-500 underline"
                >
                  {row.original.username || "N/A"}
                </Button>

                {row.original.tgId === null && (
                  <UnnasignedTgTooltip offset={-0.5} />
                )}
              </>
            )}
          </div>
        </DataTableCenteredCell>
      );
    },
  },
  {
    accessorKey: "balances",
    header: () => <DataTableCenteredCell>Баланс</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {getUSDTBalance(row.original.balances)}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalTurnover",
    header: () => <DataTableCenteredCell>Оборот</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.totalTurnover}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalLeadsCount",
    header: () => <DataTableCenteredCell>Лидов</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.totalLeadsCount ?? "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "closedLeadsCount",
    header: () => <DataTableCenteredCell>Закрытий</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.closedLeadsCount ?? "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "totalApplicationsCount",
    header: () => <DataTableCenteredCell>Всего заявок</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.totalApplicationsCount}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "approvedApplicationsCount",
    header: () => (
      <DataTableCenteredCell>Принятых заявок</DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.approvedApplicationsCount}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "rejectedApplicationsCount",
    header: () => (
      <DataTableCenteredCell>Отклоненных заявок</DataTableCenteredCell>
    ),
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.rejectedApplicationsCount ?? "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "reviewedApplicationsProfit",
    header: () => <DataTableCenteredCell>Прибыль</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {row.original.reviewedApplicationsProfit ?? "N/A"}
      </DataTableCenteredCell>
    ),
  },
  {
    accessorKey: "status",
    header: () => <DataTableCenteredCell>Статус</DataTableCenteredCell>,
    cell: ({ row }) => (
      <DataTableCenteredCell>
        {(row.original.pauseStatus?.self ?? false) ? "Пауза" : "Активный"}
      </DataTableCenteredCell>
    ),
  },
  {
    id: "actions",
    header: () => (
      <DataTableCenteredCell>Остановка выдачи</DataTableCenteredCell>
    ),
    cell: ({ row }) => {
      const { _id, pauseStatus } = row.original;
      const isPaused = pauseStatus?.head ?? false;
      const [paused, setPaused] = useState(isPaused);

      useEffect(() => {
        if (isPaused) {
          setPaused(isPaused);
        }
      }, [isPaused]);

      const { mutateAsync, isPending } = usePauseModeratorAccountHead();

      const handlePause = async () => {
        await mutateAsync({ id: _id, pauseStatus: !paused }).then(() => {
          setPaused(!paused);
        });
      };

      return (
        <DataTableCenteredCell>
          <Button
            variant={paused ? "outline" : "destructive"}
            onClick={handlePause}
            className="w-full"
          >
            {isPending && <Spinner />}
            {!isPending && (paused ? "Вернуть" : "Приостановить")}
          </Button>
        </DataTableCenteredCell>
      );
    },
  },
];
